import React, { useState, useEffect, useCallback } from "react";

/** Components */
import { ButtonGroup, Button, Tooltip, TextField, Autocomplete, Collapse, TableContainer, Paper, Table, TableHead, TableBody, TableFooter, TableRow, TableCell, TablePagination } from "@mui/material";
import CustomDatePicker from "../../commons/CustomDatePicker";
import { FilterBy } from "../../commons/filter.component";
import ButtonWithAPICall from "../../commons/ButtonWithAPICall.component";
import LoadingProgress from "../../commons/LoadingProgress.component";
import InvoiceXMLReader from "./invoiceXMLReader";
import { TreeMenu } from "../../commons/renderTreeMenu.component";

/** Icons */
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";

/** Functions & API */
import { setHeaderTitle } from "../../../store/layoutSlide";
import { getPIMenu, getPI, getOrganizationData, getDepartment } from "../../../services/api";
/** Hook */
import { useDispatch } from "react-redux";
import { useParamFilter } from "../../commons/customHook";
import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";

import moment from "moment";

/** Config */
import { statusColor } from "../../config";

/** Init Data */
const initialFilter = {
  start_date: "",
  end_date: "",
  provider_name: "",
  provider_tax: "",
  organization: null,
  department: null,
  status: null,
  apv_date: "",
};

const statusOption = [
  { label: "CHECKING", code: 0 },
  { label: "CHECKED", code: 1 },
  { label: "NOT CHECKED", code: 2 },
  { label: "APPROVED", code: 3 },
  { label: "DELETED", code: 4 },
  { label: "NOT APPROVED", code: 5 },
];

const tblHeader = [
  "STT",
  "Ngày hóa đơn",
  "Ngày duyệt",
  "Mẫu số/ký hiệu",
  "Số hóa đơn",
  "Tên người bán",
  "MST người bán",
  "T.tiền trước thuế (VND)",
  "Thuế xuất (%)",
  "Tiền thuế (VND)",
  "T.tiền sau thuế (VND)",
  "Trạng thái",
];

const PI = ({ type }) => {
  /** Hook */
  const dispatch = useDispatch();
  const [t] = useTranslation();
  const { fetchData, data, page, perPage, setPage, setPerPage, total, loading } = useParamFilter({ page: 1, per_page: 50 }, getPI);
  const [filter, setFilter] = useState({ ...initialFilter });
  const history = useHistory();

  /** State */
  const [open, setOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [treeData, setTreeData] = useState();

  /** Init header */
  useEffect(() => {
    switch (type) {
      case 0:
        dispatch(setHeaderTitle("THÔNG TIN HOÁ ĐƠN CHỜ KIỂM TRA"));
        break;
      case 1:
        dispatch(setHeaderTitle("THÔNG TIN HOÁ ĐƠN ĐÃ KIỂM TRA"));
        break;
      case 2:
        dispatch(setHeaderTitle("THÔNG TIN HOÁ ĐƠN ĐÃ DUYỆT"));
        break;
      default:
        dispatch(setHeaderTitle("THÔNG TIN HOÁ ĐƠN ĐẦU VÀO"));
        break;
    }

    /** Init data from api */
    handleSearch();

    /** Init Menu */
    getPIMenu({ type: type }).then((res) => {
      setTreeData(res.data);
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type]);

  /** Init data and search */
  const handleSearch = () => {
    fetchData({ ...filter, type: type, status: filter.status?.code });
  };

  const handleRowClick = (row) => {
    setSelectedRow(row);
  };

  const handleResponse = useCallback(
    (res) => {
      if (res.code !== 200) return;
      if (selectedRow?.id === res.data?.id) setSelectedRow(res.data);
      handleSearch();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectedRow]
  );

  const handleClose = useCallback(() => {
    setSelectedRow(null);
  }, []);

  /** Render Content */
  return (
    <div>
      {/* Header */}
      <div align="right" style={{ marginBottom: 10, marginTop: 10, backgroundColor: "#b3deff", height: 40 }}>
        <ButtonGroup style={{ height: 40, marginRight: 10 }}>
          {type === 0 && (
            <Button variant="outlined" color="primary" onClick={() => history.push("/pi/create")}>
              <Tooltip title="Tạo mới">
                <AddIcon />
              </Tooltip>
            </Button>
          )}
          <Button onClick={() => setOpen((prev) => !prev)} variant="outlined">
            <Tooltip title="Thư mục">
              <SearchIcon />
            </Tooltip>
          </Button>
        </ButtonGroup>
      </div>
      <Collapse in={open}>
        <CustomDatePicker label={t("Từ ngày")} value={filter.start_date} onChange={(value) => setFilter((prev) => ({ ...prev, start_date: value }))} sx={{ minWidth: "200px", margin: "5px" }} />
        <CustomDatePicker label={t("Đến ngày")} value={filter.end_date} onChange={(value) => setFilter((prev) => ({ ...prev, end_date: value }))} sx={{ minWidth: "200px", margin: "5px" }} />
        <CustomDatePicker label={t("Ngày duyệt")} value={filter.apv_date} onChange={(value) => setFilter((prev) => ({ ...prev, apv_date: value }))} sx={{ minWidth: "200px", margin: "5px" }} />
        <TextField
          InputProps={{ style: { minWidth: 200 } }}
          variant="outlined"
          label={t("Tên người bán")}
          value={filter.provider_name}
          onChange={(e) => setFilter((prev) => ({ ...prev, provider_name: e.target.value }))}
          style={{ margin: "5px" }}
        />
        <TextField
          InputProps={{ style: { minWidth: 200 } }}
          variant="outlined"
          label={t("Mã số thuế")}
          value={filter.provider_tax}
          onChange={(e) => setFilter((prev) => ({ ...prev, provider_tax: e.target.value }))}
          style={{ margin: "5px" }}
        />
        <FilterBy
          getDataFunc={() => getOrganizationData()}
          value={filter.organization}
          onChange={(e, newValue) => setFilter((prev) => ({ ...prev, organization: newValue }))}
          label={t("Tổ chức")}
          style={{ margin: "5px", width: 210, display: "inline-flex" }}
        />
        <FilterBy
          getDataFunc={() => getDepartment()}
          value={filter.department}
          onChange={(e, newValue) => setFilter((prev) => ({ ...prev, department: newValue }))}
          label={t("Phòng/Ban")}
          style={{ margin: "5px", width: 210, display: "inline-flex" }}
        />
        <Autocomplete
          style={{ margin: "5px", width: 210, display: "inline-flex" }}
          options={statusOption}
          getOptionLabel={(option) => t(option.label)}
          value={filter.status}
          onChange={(e, newValue) => setFilter((prev) => ({ ...prev, status: newValue }))}
          renderInput={(params) => <TextField {...params} label={t("Trạng thái")} />}
        />
        <ButtonWithAPICall api={handleSearch} disableSnackbar={() => true} variant="contained">
          {t("Tìm kiếm")}
        </ButtonWithAPICall>
      </Collapse>

      {/* Main Table */}
      <div style={{ display: "flex" }}>
        {/* Tree Menu */}
        <TreeMenu
          data={treeData}
          onClick={(query) => {
            fetchData((prev) => ({ ...query, type: prev.type, per_page: prev.per_page }));
          }}
          isExpand={true}
        />

        {loading ? (
          <LoadingProgress />
        ) : (
          <TableContainer component={Paper} style={{ width: "98%", marginLeft: 10 }} sx={{ maxHeight: 800 }}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  {tblHeader.map((item, id) => (
                    <TableCell key={id} style={{ border: "0.5px solid lightgray" }}>
                      {item}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {data &&
                  data.map((row, id) => (
                    <TableRow key={id} hover style={{ cursor: "pointer" }} onClick={(e) => handleRowClick(row)}>
                      <TableCell style={{ border: "0.5px solid lightgray" }}>{row.id}</TableCell>
                      <TableCell style={{ border: "0.5px solid lightgray" }}>{row.date && moment(row.date).format("DD/MM/YYYY")}</TableCell>
                      <TableCell style={{ border: "0.5px solid lightgray" }}>{row.apv_date && moment(row.apv_date).format("DD/MM/YYYY")}</TableCell>
                      <TableCell style={{ border: "0.5px solid lightgray" }}>
                        {row.form}/{row.serial}
                      </TableCell>
                      <TableCell style={{ border: "0.5px solid lightgray" }}>{row.code}</TableCell>
                      <TableCell style={{ border: "0.5px solid lightgray", minWidth: 200 }} align="left">
                        {row.provider_name}
                      </TableCell>
                      <TableCell style={{ border: "0.5px solid lightgray" }}>{row.provider_tax}</TableCell>
                      <TableCell style={{ border: "0.5px solid lightgray" }}>{Math.round(row.amount).toLocaleString()}</TableCell>
                      <TableCell style={{ border: "0.5px solid lightgray" }}>{row.vat_rate}</TableCell>
                      <TableCell style={{ border: "0.5px solid lightgray" }}>{Math.round(row.vat).toLocaleString()}</TableCell>
                      <TableCell style={{ border: "0.5px solid lightgray" }}>{Math.round(row.total).toLocaleString()}</TableCell>
                      <TableCell style={{ border: "0.5px solid lightgray" }}>
                        <div className={`status ${row.status}`} style={{ color: statusColor[row.status] }}>
                          {t(row.status)}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
              <TableFooter>
                <TableRow>
                  <TablePagination rowsPerPageOptions={[50, 100, 150]} count={total || 0} rowsPerPage={perPage} page={page} onPageChange={setPage} onRowsPerPageChange={setPerPage} />
                </TableRow>
              </TableFooter>
            </Table>
          </TableContainer>
        )}
      </div>

      <InvoiceXMLReader key={selectedRow?.id} onResponse={handleResponse} open={Boolean(selectedRow)} data={selectedRow} onClose={handleClose} onEditResponse={handleResponse} />
    </div>
  );
};
export default PI;
